
import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, CheckCircle, Info } from "lucide-react";
import { authNavigationController, AuthFlowState } from "@/controllers/AuthNavigationController";
import { useVerificationData } from "@/hooks/useVerificationData";
import { VerificationStep } from "@/types/verificationTypes";
import { isVerificationTrackingEnabled } from "@/services/VerificationService";

/**
 * Component that displays debug information about the current auth flow
 */
export const AuthVerificationTracker = () => {
  const [searchParams] = useSearchParams();
  const [isOpen, setIsOpen] = useState(false);
  const [flowDetails, setFlowDetails] = useState<{[key: string]: any}>({});
  const [flowState, setFlowState] = useState<AuthFlowState | null>(null);
  
  // Get verification sessions from the database (only when open)
  const { useSessionsQuery } = useVerificationData();
  const { 
    data: sessionsData, 
    isLoading: isLoadingSessions 
  } = useSessionsQuery("all", undefined, isOpen);
  
  // State for manual selection of session
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  
  // Get steps for selected session
  const { useSessionStepsQuery } = useVerificationData();
  const { 
    data: selectedSessionSteps
  } = useSessionStepsQuery(selectedSessionId);
  
  // Check if verification tracking is enabled
  const [trackingEnabled, setTrackingEnabled] = useState(false);
  
  useEffect(() => {
    setTrackingEnabled(isVerificationTrackingEnabled());
  }, []);
  
  useEffect(() => {
    // If sessions change, select the most recent one automatically
    if (sessionsData?.sessions?.length > 0 && !selectedSessionId) {
      setSelectedSessionId(sessionsData.sessions[0].session_id);
    }
  }, [sessionsData, selectedSessionId]);
  
  useEffect(() => {
    // Gather information about the current auth flow
    const gatherFlowInfo = () => {
      try {
        const info: {[key: string]: any} = {
          currentUrl: window.location.href,
          urlParams: Object.fromEntries(searchParams.entries()),
          timestamp: new Date().toISOString(),
          localStorage: {}
        };
        
        // Safely get auth flow related localStorage items
        const authRelatedKeys = [
          'lamulamu_auth_flow_type',
          'lamulamu_auth_flow_timestamp',
          'lamulamu_pwd_reset_completed',
          'lamulamu_auth_flow_state'
        ];
        
        authRelatedKeys.forEach(key => {
          const value = localStorage.getItem(key);
          if (value) {
            info.localStorage[key] = value;
          }
        });
        
        // Get the current state from the controller
        const controllerState = authNavigationController.getState();
        setFlowState(controllerState);
        info.controllerState = controllerState;
        
        setFlowDetails(info);
      } catch (err) {
        console.error("Error gathering flow info:", err);
      }
    };
    
    gatherFlowInfo();
    
    // Update every 3 seconds while open
    const interval = isOpen ? setInterval(gatherFlowInfo, 3000) : null;
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isOpen, searchParams]);

  // Auto-open in certain circumstances
  useEffect(() => {
    const token = searchParams.get('token') || searchParams.get('code');
    const type = searchParams.get('type');
    const view = searchParams.get('view');
    
    // Auto-open for verification flows
    if (token || (view === 'resetPassword') || type === 'recovery' || type === 'signup') {
      setIsOpen(true);
    }
  }, [searchParams]);

  const getStepIcon = (status: string) => {
    switch (status) {
      case 'error':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      case 'success':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      default:
        return <Info className="h-4 w-4 text-blue-500" />;
    }
  };

  // Check if we should show the tracker
  const shouldShowTracker = process.env.NODE_ENV === "development" || trackingEnabled;
  
  if (!shouldShowTracker) {
    return null;
  }

  return (
    <div className="fixed bottom-4 left-4 z-50">
      <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
        <div 
          className="flex justify-between items-center px-4 py-2 bg-gray-50 border-b border-gray-200 cursor-pointer"
          onClick={() => setIsOpen(!isOpen)}
        >
          <div className="font-medium">Auth Flow Tracker</div>
          <Button 
            variant="ghost" 
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              setIsOpen(!isOpen);
            }}
          >
            {isOpen ? "↑" : "↓"}
          </Button>
        </div>
        
        {isOpen && (
          <div className="p-4">
            <div className="mb-2">
              <div className="font-medium">Current URL</div>
              <div className="text-xs break-all bg-gray-100 p-1 rounded">{flowDetails.currentUrl}</div>
            </div>
            
            {flowState && flowState.type && (
              <div className="mb-2">
                <div className="font-medium">Auth Flow State Machine</div>
                <div className="text-xs bg-gray-100 p-2 rounded mb-1">
                  <div><span className="font-medium">Type:</span> {flowState.type}</div>
                  <div><span className="font-medium">Stage:</span> {flowState.stage}</div>
                  {flowState.email && <div><span className="font-medium">Email:</span> {flowState.email}</div>}
                  {flowState.userId && <div><span className="font-medium">User ID:</span> {flowState.userId}</div>}
                  <div><span className="font-medium">Updated:</span> {new Date(flowState.timestamp).toLocaleTimeString()}</div>
                </div>
              </div>
            )}
            
            {flowDetails.urlParams && Object.keys(flowDetails.urlParams).length > 0 && (
              <div className="mb-2">
                <div className="font-medium">URL Parameters</div>
                <div className="text-xs">
                  {Object.entries(flowDetails.urlParams).map(([key, value]) => (
                    <div key={key}><span className="font-medium">{key}:</span> {String(value)}</div>
                  ))}
                </div>
              </div>
            )}
            
            {flowDetails.localStorage && Object.keys(flowDetails.localStorage).length > 0 && (
              <div className="mb-2">
                <div className="font-medium">Auth Flow State</div>
                <div className="text-xs">
                  {Object.entries(flowDetails.localStorage).map(([key, value]) => (
                    <div key={key}><span className="font-medium">{key}:</span> {String(value)}</div>
                  ))}
                </div>
              </div>
            )}
            
            {isLoadingSessions ? (
              <div className="text-center p-2">Loading sessions...</div>
            ) : sessionsData?.sessions && sessionsData.sessions.length > 0 ? (
              <div>
                <div className="font-medium mb-1">Verification Sessions</div>
                
                {/* Session selector */}
                <div className="mb-2">
                  <select 
                    className="w-full p-1 text-xs border rounded"
                    value={selectedSessionId || ''}
                    onChange={(e) => setSelectedSessionId(e.target.value)}
                  >
                    <option value="">Select a session</option>
                    {sessionsData.sessions.map(session => (
                      <option key={session.session_id} value={session.session_id}>
                        {session.label || 'Unnamed'} - {new Date(session.start_timestamp).toLocaleTimeString()}
                      </option>
                    ))}
                  </select>
                </div>
                
                {/* Display steps for selected session */}
                {selectedSessionId && selectedSessionSteps ? (
                  <ScrollArea className="h-60 w-80">
                    <div className="space-y-1">
                      {selectedSessionSteps.map((step: VerificationStep, index: number) => (
                        <div key={index} className="flex items-start gap-2 text-xs border-b pb-1">
                          <div className="mt-0.5">{getStepIcon(step.status)}</div>
                          <div>
                            <span className="font-medium">{step.step}</span>
                            <div className="text-gray-500">
                              {new Date(step.timestamp).toLocaleTimeString()}
                            </div>
                            {step.details && Object.keys(step.details).length > 0 && (
                              <div className="text-gray-600 mt-0.5">
                                {Object.entries(step.details).map(([key, value]) => (
                                  <div key={key}>{key}: {String(value)}</div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                      
                      {selectedSessionSteps.length === 0 && (
                        <div className="text-center p-2 text-gray-500">No steps found for this session</div>
                      )}
                    </div>
                  </ScrollArea>
                ) : (
                  <div className="text-center p-2 text-gray-500">Select a session to view steps</div>
                )}
              </div>
            ) : (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  No verification sessions found for this flow.
                </AlertDescription>
              </Alert>
            )}
            
            <div className="mt-3 flex justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  // Clear auth-related localStorage and controller state
                  authNavigationController.clearState();
                  // Update the display
                  setFlowState(null);
                  setFlowDetails({
                    ...flowDetails,
                    localStorage: {},
                    controllerState: null
                  });
                }}
              >
                Clear Auth State
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
